import { GLOBAL_PORTAL_ELEMENT_ID } from './consts';

const OLD_CB_GLOBAL_PORTAL_ELEMENT_ID = 'cb-portal';

// eslint-disable-next-line unicorn/prefer-global-this
const canUseDOM = !(typeof window !== 'object' || !document);

const globalMountElements: Record<string, HTMLDivElement | null> = {};

type Layer = 'default' | 'top';

const setStyles = (el: HTMLDivElement) => {
  // Fixed position is required for the portal so that overflowing content will not cause
  // the page to scroll. Note that because of this the content inside the portal can get
  // hidden if positioning is not handled correctly.
  el.style.position = 'fixed';
  // Setting `insetInline` is required for the elements inside the portal to be able to
  // scale freely in width.
  el.style.insetInline = '0';
  // Setting `top` ensures that the portal is at the top of the page. This solved an issue
  // with the menu positioning with `react-select`.
  el.style.top = '0';
};

/**
 * Utility to return (and create if not created already) the mounting element for Alchemy portaled
 * components.
 *
 * @param layer - The "layer" to get the portal element for. Top layer may be used to ensure that
 *   the content is rendered above everything else.
 */
export const getPortalElement = (layer: Layer = 'default'): HTMLDivElement | null => {
  if (!canUseDOM) return null;

  let mountElement = globalMountElements[layer];
  if (mountElement) return mountElement;

  if (layer === 'top') {
    // Ensure that the default layer is created before the top layer.
    const defaultLayer = getPortalElement() as HTMLDivElement;

    mountElement = document.createElement('div');
    mountElement.id = `${GLOBAL_PORTAL_ELEMENT_ID}-top`;
    setStyles(mountElement);
    globalMountElements.top = mountElement;

    defaultLayer.after(mountElement);
    return mountElement;
  }

  mountElement =
    document.querySelector(`#${OLD_CB_GLOBAL_PORTAL_ELEMENT_ID}`) ??
    document.querySelector(`#${GLOBAL_PORTAL_ELEMENT_ID}`);

  if (mountElement) {
    // Ensure that the portal has correct position even if it was created
    // by a very old Cookbook version of this utility.
    if (mountElement.id === OLD_CB_GLOBAL_PORTAL_ELEMENT_ID && !mountElement.style.position) {
      setStyles(mountElement);
    }

    globalMountElements[layer] = mountElement;
    return mountElement;
  }

  // Nothing found, create the mount element.
  mountElement = document.createElement('div');
  mountElement.id = GLOBAL_PORTAL_ELEMENT_ID;
  setStyles(mountElement);

  document.body.append(mountElement);

  return mountElement;
};
